/**
 * Kjuup.io trial countdown math (mobile parity: Kjuup-Mobile-App/src/utils/trialCountdown.js).
 *
 * Everything is derived from Stripe's trial_end as returned by
 * GET /payment/subscription/status, shifted by the server clock offset so a
 * wrong device clock cannot move the countdown.
 */

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/** serverNow (ISO) minus the browser clock at the moment the status arrived. */
export function clockOffsetFromServer(serverNow, receivedAt = Date.now()) {
  const server = serverNow ? Date.parse(serverNow) : NaN;
  return Number.isFinite(server) ? server - receivedAt : 0;
}

/** Milliseconds left until trialEnd (never negative), or null without an end. */
export function trialMsLeft(trialEnd, clockOffsetMs = 0, now = Date.now()) {
  const end = trialEnd ? new Date(trialEnd).getTime() : NaN;
  if (!Number.isFinite(end)) return null;
  return Math.max(0, end - (now + (Number(clockOffsetMs) || 0)));
}

/** Whole days left, rounded up the same way the backend counts them. */
export function trialDaysLeft(msLeft) {
  if (msLeft == null) return null;
  return Math.ceil(msLeft / DAY_MS);
}

/**
 * The single unit to show during the last 24 hours: hours, then minutes, then
 * seconds. Null outside the last day and once the trial has ended.
 */
export function lastDayUnit(msLeft) {
  if (msLeft == null || msLeft <= 0 || msLeft > DAY_MS) return null;
  if (msLeft >= HOUR_MS) return { unit: "hour", count: Math.floor(msLeft / HOUR_MS) };
  if (msLeft >= MINUTE_MS) return { unit: "minute", count: Math.floor(msLeft / MINUTE_MS) };
  return { unit: "second", count: Math.max(1, Math.ceil(msLeft / SECOND_MS)) };
}

const UNIT_KEYS = {
  hour: ["kjuup_io_trial_left_hour", "kjuup_io_trial_left_hours", "1 hour remaining", "{count} hours remaining"],
  minute: ["kjuup_io_trial_left_minute", "kjuup_io_trial_left_minutes", "1 minute remaining", "{count} minutes remaining"],
  second: ["kjuup_io_trial_left_second", "kjuup_io_trial_left_seconds", "1 second remaining", "{count} seconds remaining"],
};

/**
 * Human label for the trial state:
 *   more than a day → "{days} days left in your free trial"
 *   last day        → "5 hours remaining" / "12 minutes remaining" / "8 seconds remaining"
 *   ended           → "Your trial is ending…" (until the status refetch lands)
 */
export function formatTrialCountdown(msLeft, strings) {
  if (msLeft == null) return "";
  const s = strings || {};
  if (msLeft <= 0) return s.kjuup_io_trial_ending_now || "Your trial is ending…";
  const last = lastDayUnit(msLeft);
  if (last) {
    const [oneKey, manyKey, oneFallback, manyFallback] = UNIT_KEYS[last.unit];
    if (last.count === 1) return s[oneKey] || oneFallback;
    return String(s[manyKey] || manyFallback).replace(/\{count\}/g, String(last.count));
  }
  return String(s.kjuup_io_trial_days_left || "{days} days left in your free trial").replace(
    /\{days\}/g,
    String(trialDaysLeft(msLeft))
  );
}
